import { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next"
import { Button, Col, Form, Input, InputNumber, Row } from 'antd';
import { InfoCircleOutlined } from "@ant-design/icons";

import HeroPageTemplate from 'components/dumb/HeroPageTemplate';
import UserForm from 'components/smart/UserForm';
import { LanguageSelect } from 'components/common/LanguageSelect';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import endpoints from 'services/apis/auth';
import { validators } from './validate';

import './style.scss';
import { storage } from 'services/config/storage';
import { useIsMobileDevice } from 'hooks';
import { COUNTRY_DATA, DEFAULT_COUNTRY_DATA, GODREJ_SECTION_IDS, LOGIN_TYPE, ORG_SET_EVENT, POST_WORKSHOP_GODREJ, PRE_WORKSHOP_GODREJ } from 'constants/globalConstants';
import PhoneInput from 'components/smart/PhoneInput';
import { ErrorWarning } from 'assets/icons';
import { CustomTooltip } from 'components/dumb/CustomTooltip';
import { useGameplayConfig } from 'store';
import analytics from 'services/config/analytics';
import useLoginType from 'views/hooks/useLoginType';

const FORM_INITIAL_VALUES = {
  name: '',
  email: '',
  candidateKey: '',
  phone: undefined,
  experience: null,
};

const formItemLayout = {
  wrapperCol: { span: 24 },
};

const PHONE_DRAFT_KEY = 'signup-phone-draft';

/** The number a candidate typed on this assessment's sign-up step, so that coming back here
 * from the OTP step leaves the phone field filled in instead of empty. */
const readPhoneDraft = (positionId) => {
  try {
    const draft = JSON.parse(sessionStorage.getItem(PHONE_DRAFT_KEY));
    return draft?.positionId === positionId ? draft : null;
  } catch (err) {
    console.error('Error reading phone draft ::', err);
    return null;
  }
};

const writePhoneDraft = (draft) => {
  try {
    sessionStorage.setItem(PHONE_DRAFT_KEY, JSON.stringify(draft));
  } catch (err) {
    console.error('Error saving phone draft ::', err);
  }
};

const SignUp = () => {

  const { validateEmail, handleKeyPress } = validators;
  const companyLogo = storage.get.companiesLogos();

  const [userDetailForm] = Form.useForm();
  const { orgType, orgId, positionId } = useParams();
  const isMobile = useIsMobileDevice();
  const history = useHistory();
  const location = useLocation()
  const { t } = useTranslation()
  const { languagesOptions, fetchConfig, configLanguageSettings, isLoading } = useGameplayConfig()
  const { isRtlLayout, layoutDirection } = configLanguageSettings;
  const loginType = useLoginType(isLoading, positionId);
  const orgInfo = storage.get.orgInfo()

  const hasPhoneLogin = (loginType === LOGIN_TYPE.PHONE);
  const hasThirdPartyLogin = (loginType === LOGIN_TYPE.THIRD_PARTY);

  // Check and make changes for godrej ids
  const hasGodrejPosition = GODREJ_SECTION_IDS.includes(positionId);
  const isPostWorkshopPosition = POST_WORKSHOP_GODREJ.includes(positionId)
  const isPreWorkshopPosition = PRE_WORKSHOP_GODREJ.includes(positionId)
  const heroPageTemplateTitle = hasGodrejPosition ? isPreWorkshopPosition ? "GPL Alchemy || Operations Academy || Concrete Formwork Masters || Pre Workshop Assessment"
    : isPostWorkshopPosition ? "GPL Alchemy || Operations Academy || Concrete Formwork Masters || Post Workshop Assessment"
      : "Operations Knowledge Assessment" :
    isMobile ? [t("welcome_aboard.welcome"), t("welcome_aboard.aboard")] : t("welcome_aboard.welcome") + t("welcome_aboard.aboard");

  const [signUpLink, setSignUpLink] = useState(null);
  const [country, setCountry] = useState(DEFAULT_COUNTRY_DATA);
  const [signupError, setSignupError] = useState(null);
  const [submitting, setSubmitting] = useState(false)

  // ===================
  // functions
  // ===================
  const redirectToOTP = ({ linkId, email, candidateKey, phone = null, country = {} }) => {
    storage.set.configStat(true);
    history.push(`/home/${linkId}`, {
      email,
      posId: positionId,
      ...((hasPhoneLogin || phone) && { phone }),
      ...((hasPhoneLogin || phone) && { country }),
      ...(hasThirdPartyLogin && { candidateKey })
    });
  };

  const handleFormSubmition = async (formData) => {
    analytics.track('signup_submitted', { login_type: loginType });
    try {
      setSubmitting(true)
      if (!formData.phone) {
        delete formData.phone;
      } else {
        formData.phone = String(formData.phone);
      }
      if (formData.phone) writePhoneDraft({ positionId, phone: formData.phone, country });
      if (!hasThirdPartyLogin) delete formData.candidateKey;
      if (!formData.email) delete formData.email;
      const payload = {
        ...formData,
        positionId,
        ...(formData.phone && { countryCode: country.phone }),
      };
      const response = hasThirdPartyLogin ?
        await endpoints.thirdPartySignup(orgType, orgId, payload) :
        await endpoints.signup(orgType, orgId, payload);
      if (response.success) {
        const { link } = response;
        const [_, linkId] = link.split('/home/');
        setSignUpLink({
          linkId,
          email: formData.email || "",
          posId: positionId,
          ...((hasPhoneLogin || formData.phone) && { phone: formData.phone }),
          ...((hasPhoneLogin || formData.phone) && { country }),
          ...(hasThirdPartyLogin && { candidateKey: formData.candidateKey })
        });
      }
    } catch (err) {
      console.log('err :>> ', err);
      // user already present: redirect to same otp screen
      if (err.metaData?.linkId) {
        redirectToOTP({
          linkId: err.metaData.linkId,
          email: formData.email || "",
          posId: positionId,
          ...((hasPhoneLogin || formData.phone) && { phone: formData.phone }),
          ...((hasPhoneLogin || formData.phone) && { country }),
          ...(hasThirdPartyLogin && { candidateKey: formData.candidateKey })
        });
      } else {
        analytics.track('signup_error', { error: err?.message });
        setSignupError(err?.message);
        console.error('Error in signup: ', err);
      }
    } finally {
      setSubmitting(false)
    }
  };

  // ===================
  // useEffects
  // ===================
  useEffect(() => {
    analytics.track('signup_started', { org_id: orgId, position_id: positionId, login_type: loginType });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**Dynamic country code from org config as per Neha. */
  useEffect(() => {
    if (location?.state?.redirectedFromRandomizer) {
      userDetailForm.setFieldsValue({ phone: location?.state?.phone })
      setCountry(location?.state?.country)
      window.history.replaceState(null, "")
      fetchConfig(false)
    } else {
      const phoneDraft = readPhoneDraft(positionId)
      const countryCode = phoneDraft?.country?.phone ?? orgInfo?.organizationConfig?.countryCode ?? "91"
      const country = COUNTRY_DATA?.find(cuntry => cuntry?.phone == countryCode)
      setCountry(country)
      if (phoneDraft?.phone) userDetailForm.setFieldsValue({ phone: phoneDraft.phone })
    }
  }, []);

  const emailFieldRequired = !(hasPhoneLogin) || hasGodrejPosition;

  return !signUpLink ? (
    <section className={`${isMobile ? "home-container" : "home-container overflowX-hidden bg-color-primary flex flex-col justify-between items-center pt-25 pb-25"}`} dir={layoutDirection}>
      <HeroPageTemplate
        headingSize={hasGodrejPosition ? 40 : 60}
        companyLogo={companyLogo}
        isMobile={isMobile}
        heading={heroPageTemplateTitle}
        content={t("login_screen.content")}
        hasGodrejPosition={hasGodrejPosition}
      >
        <UserForm
          {...formItemLayout}
          form={userDetailForm}
          autoComplete="off"
          autoCapitalize="off"
          className="signup-form"
          submitForm={handleFormSubmition}
          formInitalValues={FORM_INITIAL_VALUES}
        >
          <div className={`${isMobile ? "" : "form"}`}>
            {
              signupError ?
                <p className="error color-primary size-14 mb-10 p-10 flex items-center">
                  <span className='flex items-center mr-4'>
                    <ErrorWarning />
                  </span>
                  {signupError}
                </p> : <></>
            }
            <Row gutter={[10, 15]}>
              <Col xs={{ span: 22 }} lg={{ span: 11 }}>
                <LanguageSelect
                  visible={languagesOptions?.length > 0}
                  className="mb-14"
                  selectClassName="w-full"
                />
              </Col>
            </Row>
            <Row gutter={[10, 15]}>
              <Col xs={{ span: 22 }} lg={{ span: 11 }}>
                <Form.Item
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: t("login_screen.name_empty"),
                    },
                  ]}
                >
                  <Input placeholder={t("login_screen.name_placeholder")} />
                </Form.Item>
              </Col>
              {
                hasThirdPartyLogin &&
                <Col xs={{ span: 22 }} lg={{ span: 11 }}>
                  <Form.Item
                    name="candidateKey"
                    rules={[
                      {
                        required: true,
                        message: t("login_screen.candidatekey_empty"),
                      },
                    ]}
                  >
                    <Input
                      placeholder={t("login_screen.candidatekey_placeholder")}
                      onPaste={({ target: { value } }) => { userDetailForm.setFieldsValue({ candidateKey: value.trim() }); }}
                      onChange={({ target: { value } }) => { userDetailForm.setFieldsValue({ candidateKey: value.trim() }); }}
                    />
                  </Form.Item>
                </Col>
              }
              <Col xs={{ span: 22 }} lg={{ span: 11 }}>
                <Form.Item
                  name="email"
                  rules={[
                    {
                      required: emailFieldRequired,
                      message: t("login_screen.email_empty"),
                    },
                    {
                      validator: (_, val) => validateEmail(_, val, t),
                    },
                  ]}
                >
                  <Input
                    placeholder={`${emailFieldRequired ? t("login_screen.email_placeholder") : t("login_screen.email_placeholder") + t("optional")}`}
                    onPaste={({ target: { value } }) => { userDetailForm.setFieldsValue({ email: value.trim() }); }}
                    onChange={({ target: { value } }) => { userDetailForm.setFieldsValue({ email: value.trim() }); }}
                  />
                </Form.Item>
              </Col>
              {
                hasGodrejPosition &&
                <div className='email-info-tooltip-container' style={{ position: 'relative' }}>
                  <CustomTooltip
                    title={`Please mention your Godrej Email ID. 
                    Only if you do not have a Godrej Email ID, please mention your personal email ID. Do NOT mention NA.`
                    }
                    showArrow={false}
                  >
                    <InfoCircleOutlined style={{ fontSize: '24px', color: 'white', position: "relative", top: "14px", cursor: "pointer" }} />
                  </CustomTooltip>
                </div>
              }
              <Col xs={{ span: 22 }} lg={{ span: 11 }}>
                <Form.Item name="phone">
                  <PhoneInput
                    placeholder={hasPhoneLogin ? t("login_screen.phone_placeholder") + "*" : t("login_screen.phone_placeholder")}
                    className="signup-phone"
                    overlayClassName="signup-form"
                    setValue={(value) => userDetailForm.setFieldValue("phone", value)}
                    country={country}
                    hasPhoneLogin={hasPhoneLogin}
                    setCountry={setCountry}
                  />
                </Form.Item>
              </Col>
              {
                !hasThirdPartyLogin &&
                <Col xs={{ span: 22 }} lg={{ span: 11 }} className={layoutDirection === "rtl" ? "yearsOfExperience-rtl" : ""}>
                  {/* <Form.Item
                    name="yearsOfExperience"
                    rules={[
                      {
                        required: true,
                        message: t("login_screen.experience_empty"),
                      },
                    ]}
                  >
                    <InputNumber
                      name="yearsOfExperience"
                      controls={false}
                      step={0.1}
                      min={0}
                      onKeyPress={handleKeyPress}
                      placeholder={t("login_screen.experience_placeholder") + "*"}
                    />
                  </Form.Item> */}
                </Col>
              }
              <Col xs={{ span: 22 }} lg={{ span: 11 }}>
                <Form.Item className={`signup-continue-btn flex ${isMobile ? 'mt-10' : ''}`}>
                  <Button loading={submitting} className="" type="primary" htmlType="submit">
                    {t("webcam.continue")}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </div>
        </UserForm>
      </HeroPageTemplate>
    </section>
  ) : (
    <HeroPageTemplate
      companyLogo={companyLogo}
      heading={t("login_screen.signup_success")}
      content={t("login_screen.continue_after_signup")}
      headingSize={60}
      isMobile={isMobile}
      hasGodrejPosition={hasGodrejPosition}
    >
      <div className={`flex ${isMobile ? 'mt-10' : 'justify-center mt-22'}`}>
        <Button
          type="primary"
          onClick={() => redirectToOTP(signUpLink)}
        >
          {t("webcam.continue")}
        </Button>
      </div>
    </HeroPageTemplate>
  );
};

export default SignUp;
